/**
 * Invisible "which key produced this string" payload for the inline editor.
 *
 * When a signed-in editor activates edit mode, the i18n wrapper (`@/lib/intl`)
 * appends one of these payloads to every `t()` string so the activation-time
 * stamp pass can read the exact `namespace.key` off the rendered text node and
 * write it as `data-i18n-key` — turning the editor's value→key *guess* (which
 * needs the "on this page" disambiguation radio for shared values) into an exact
 * lookup. The payload is then stripped from the text node. (AHD-7514)
 *
 * The payload is built only from zero-width / invisible code points, so the
 * rendered text is visually byte-identical and copy-paste only carries
 * characters that don't print. It NEVER appears in the public build: server
 * `t()` is pure passthrough and the client wrapper only stamps once an
 * authenticated editor flips stamp mode on — see `lib/intl/stamp-mode.ts`.
 */

// All four markers are zero-width and render to nothing. Bits encode the key's
// UTF-16 code units (16 bits each, MSB first) so any character round-trips.
const START = "⁢" // INVISIBLE TIMES — payload opens
const END = "⁣" // INVISIBLE SEPARATOR — payload closes
const BIT0 = "​" // ZERO WIDTH SPACE
const BIT1 = "‌" // ZERO WIDTH NON-JOINER

/** Every code point the payload is built from — used to strip it back out. */
const SENTINEL_CHARS = [START, END, BIT0, BIT1]

/** True if `text` contains a (possibly mangled) render-key payload. */
export function hasRenderKey(text: string): boolean {
  return text.includes(START)
}

/** Wrap a dotted message key as an invisible, appendable payload. */
export function encodeRenderKey(key: string): string {
  let bits = ""
  for (let i = 0; i < key.length; i++) {
    const code = key.charCodeAt(i)
    for (let b = 15; b >= 0; b--) bits += (code >> b) & 1 ? BIT1 : BIT0
  }
  return START + bits + END
}

/**
 * Pull the key back out of a string that carries a payload. Returns the decoded
 * `key` plus `clean` (the visible text with every sentinel code point removed),
 * or `null` when there's no well-formed payload. Tolerant of trailing junk: it
 * decodes the first START…END frame and strips all sentinel chars from the rest.
 */
export function extractRenderKey(
  text: string
): { key: string; clean: string } | null {
  const start = text.indexOf(START)
  if (start === -1) return null
  const end = text.indexOf(END, start + 1)
  if (end === -1) return null
  const bits = text.slice(start + 1, end)
  // A frame is 16 bits per character; a wrong length means a mangled payload.
  if (bits.length === 0 || bits.length % 16 !== 0) return null
  let key = ""
  for (let i = 0; i < bits.length; i += 16) {
    let code = 0
    for (let b = 0; b < 16; b++) {
      const ch = bits[i + b]
      if (ch === BIT1) code = (code << 1) | 1
      else if (ch === BIT0) code = code << 1
      else return null // foreign char inside the frame → not our payload
    }
    key += String.fromCharCode(code)
  }
  return { key, clean: stripRenderKey(text) }
}

/** Remove every sentinel code point, returning the bare visible text. */
export function stripRenderKey(text: string): string {
  if (!text.includes(START) && !text.includes(BIT0) && !text.includes(BIT1))
    return text
  let out = text
  for (const ch of SENTINEL_CHARS) out = out.split(ch).join("")
  return out
}
